import api from "../../api/client";
import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Table,
  Card,
  Row,
  Col,
  Statistic,
  DatePicker,
  Select,
  Space,
  Spin,
  Typography,
  Tag,
  Tooltip,
  Button,
  message,
  Divider,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { Link } from "react-router-dom";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title } = Typography;

const EggCollectionsDashboard = () => {
  const account = useSelector((state) => state.account);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [batchOptions, setBatchOptions] = useState([]);
  const [filters, setFilters] = useState({
    batch: null,
    dateRange: null,
    urlToUse: "current",
  });

  const urlOptions = [
    { value: "current", label: "Current Inventory" },
    { value: "history", label: "Collections History" },
  ];

  // Fetch batch options for filter
  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const response = await api.get(`/api/animals/poultry/layers`);
        const data = response.data.data;
        // Filter only Layers if needed
        const batches = data.map((item) => ({
          value: item.batchId,
          label: `${item.batchId} (${item.farm_name})`,
        }));
        setBatchOptions(batches);
      } catch (error) {
        console.error("Error fetching batches:", error);
        message.error("Failed to load batches");
      }
    };
    fetchBatches();
  }, [api]);

  let url = `/eggs/collections`;

  if (filters.urlToUse === "history") {
    url = `/eggs/collections-history`;
  }

  // Fetch egg collections based on filters
  const fetchCollections = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.batch) params.batch = filters.batch;
      if (filters.dateRange) {
        params.startDate = filters.dateRange[0].format("YYYY-MM-DD");
        params.endDate = filters.dateRange[1].format("YYYY-MM-DD");
      }
      const response = await api.get(url, {
        params,
      });
      setData(response.data);
    } catch (error) {
      console.error("Error fetching collections:", error);
      message.error("Failed to load collections");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, [filters]);

  const totals = data.reduce(
    (acc, item) => {
      acc.normal.trays += item.normal_trays || 0;
      acc.normal.eggs += item.normal_eggs || 0;

      acc.broken.trays += item.broken_trays || 0;
      acc.broken.eggs += item.broken_eggs || 0;

      acc.doubleYolk.trays += item.double_yolk_trays || 0;
      acc.doubleYolk.eggs += item.double_yolk_eggs || 0;

      acc.medium.trays += item.medium_trays || 0;
      acc.medium.eggs += item.medium_eggs || 0;

      acc.spoilt.trays += item.spoilt_trays || 0;
      acc.spoilt.eggs += item.spoilt_eggs || 0;

      return acc;
    },
    {
      normal: { trays: 0, eggs: 0 },
      broken: { trays: 0, eggs: 0 },
      doubleYolk: { trays: 0, eggs: 0 },
      medium: { trays: 0, eggs: 0 },
      spoilt: { trays: 0, eggs: 0 },
    },
  );

  // Table columns
  const columns = [
    {
      title: "Date",
      dataIndex: "recorded",
      key: "recorded",
      render: (text) => moment(text).format("YYYY-MM-DD"),
      sorter: (a, b) => moment(a.recorded).unix() - moment(b.recorded).unix(),
      defaultSortOrder: "descend",
    },
    {
      title: "Batch",
      dataIndex: "batch",
      key: "batch",
      filters: batchOptions.map((opt) => ({
        text: opt.label,
        value: opt.value,
      })),
      onFilter: (value, record) => record.batch === value,
    },
    // {
    //   title: "Farm",
    //   dataIndex: "farm",
    //   key: "farm",
    // },
    {
      title: "Normal",
      dataIndex: "normal_trays",
      key: "normal_trays",
      sorter: (a, b) => a.normal_trays - b.normal_trays,
      render: (val) => val?.toLocaleString(),
    },
    {
      title: "N. Remainder",
      dataIndex: "normal_eggs",
      key: "normal_eggs",
      sorter: (a, b) => a.normal_eggs - b.normal_eggs,
      render: (val) => val?.toLocaleString(),
    },

    {
      title: "Broken",
      dataIndex: "broken_trays",
      key: "broken_trays",
      render: (val) => val?.toLocaleString(),
    },
    {
      title: "Double Yolk",
      dataIndex: "double_yolk_trays",
      key: "double_yolk_trays",
      render: (val) => val?.toLocaleString(),
    },
    {
      title: "Medium",
      dataIndex: "medium_trays",
      key: "medium_trays",
      render: (val) => val?.toLocaleString(),
    },
    {
      title: "Spoilt",
      dataIndex: "spoilt_trays",
      key: "spoilt_trays",
      render: (val) => val?.toLocaleString(),
    },
    {
      title: "Laying Period",
      key: "layingPeriod",
      render: (_, record) => {
        const months = record.monthsLaying || 0;
        return (
          <Tooltip title={`Batch has been laying for ${months} months`}>
            <Tag
              color={months >= 18 ? "red" : months >= 16 ? "orange" : "green"}
            >
              {months} months
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Replacement Status",
      key: "replacementStatus",
      render: (_, record) => {
        const status = record.replacementStatus || "OK";
        return (
          <Tag
            color={getReplacementStatusColor(status)}
            icon={getReplacementStatusIcon(status)}
          >
            {status}
          </Tag>
        );
      },
    },
    {
      title: "Production Change",
      key: "productionChange",
      render: (_, record) => {
        const change = record.previousProduction;
        console.log("prod", record);
        if (!change) return <Tag color="default">N/A</Tag>;
        const isPositive = parseFloat(change) > 0;
        return (
          <Tag color={isPositive ? "success" : "error"}>
            {isPositive ? "+" : ""}
            {change}%
          </Tag>
        );
      },
    },
    // {
    //   title: 'Quality',
    //   key: 'quality',
    //   render: (_, record) => {
    //     const quality = record.normalPercentage || 0;
    //     return (
    //       <Tooltip title={`${quality}% normal eggs`}>
    //         <Tag color={quality >= 80 ? 'success' : quality >= 60 ? 'warning' : 'error'}>
    //           {quality >= 80 ? 'Excellent' : quality >= 60 ? 'Good' : 'Needs Attention'}
    //         </Tag>
    //       </Tooltip>
    //     );
    //   },
    // },

    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      ellipsis: true,
      render: (text) => text || "-",
    },
    {
      title: "Recorded By",
      dataIndex: "userName",
      key: "userName",
    },
    {
      title: "Updated",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => moment(text).fromNow(),
      sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
      defaultSortOrder: "descend",
    },
  ];

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({ batch: null, dateRange: null });
  };

  const csvLinkRef = useRef();

  const exportToCSVs = () => {
    const csvData = [
      [
        "ID",
        "Batch No.",
        "Normal Eggs",
        "Broken",
        "Double Yolk",
        "Middle Sized",
        "Spoilt",
        "Date",
        "Updated",
        "Notes",
      ],
      ...data.map((item) => [
        item.id,
        item.batch,
        item.normal_trays,
        item.broken_trays,
        item.double_yolk_trays,
        item.medium_trays,
        item.spoilt_trays,
        new Date(item.recorded).toLocaleDateString(),
        new Date(item.createdAt).toLocaleDateString(),
        item.remarks,
      ]),
    ];

    const csvContent = csvData.map((row) => row.join(",")).join("\n");
    const blob = new Blob(["\ufeff", csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const csvUrl = URL.createObjectURL(blob);
    csvLinkRef.current.href = csvUrl;
    csvLinkRef.current.download = "sfarms.csv";
    csvLinkRef.current.click();
  };

  const EGGS_PER_TRAY = 30;

  /* ---------------- HELPERS ---------------- */

  const getTotalEggs = (trays = 0, eggs = 0) => trays * EGGS_PER_TRAY + eggs;

  const normalizeEggs = (trays = 0, eggs = 0) => {
    const totalEggs = getTotalEggs(trays, eggs);

    return {
      trays: Math.floor(totalEggs / EGGS_PER_TRAY),
      eggs: totalEggs % EGGS_PER_TRAY,
      totalEggs,
    };
  };

  const formatTrayLabel = (trays) =>
    `${trays} ${trays === 1 ? "tray" : "trays"}`;

  /* ---------------- CATEGORY DATA ---------------- */

  const categoryData = {};

  Object.keys(totals).forEach((key) => {
    const normalized = normalizeEggs(totals[key].trays, totals[key].eggs);

    categoryData[key] = {
      ...normalized,
    };
  });

  /* ---------------- TOTAL EGGS ALL ---------------- */

  const totalEggsAll = Object.values(categoryData).reduce(
    (sum, cat) => sum + cat.totalEggs,
    0,
  );

  /* ---------------- ADD PERCENTAGES ---------------- */

  Object.keys(categoryData).forEach((key) => {
    categoryData[key].percentage =
      totalEggsAll > 0
        ? ((categoryData[key].totalEggs / totalEggsAll) * 100).toFixed(1)
        : 0;
  });

  /* ---------------- TOTAL COLLECTED ---------------- */

  const totalCollectedRaw = Object.values(categoryData).reduce(
    (acc, cat) => {
      acc.trays += cat.trays;
      acc.eggs += cat.eggs;
      return acc;
    },
    { trays: 0, eggs: 0 },
  );

  const totalCollected = normalizeEggs(
    totalCollectedRaw.trays,
    totalCollectedRaw.eggs,
  );

  totalCollected.percentage = 100;

  const EggStatCard = ({ title, data, color }) => (
    <>
      <Statistic
        title={title}
        value={formatTrayLabel(data.trays)}
        valueStyle={{ color }}
      />

      <div style={{ fontSize: 14, color: "#888" }}>
        {data.eggs} {data.eggs === 1 ? "loose egg" : "loose eggs"}
      </div>

      <div style={{ fontSize: 14, marginTop: 4 }}>
        Total Eggs: <strong>{data.totalEggs}</strong>
      </div>

      <div style={{ fontSize: 14, color: "#888" }}>
        {data.percentage}% of collection
      </div>
    </>
    // </Card>
  );

  // Helper functions for replacement status
  const getReplacementStatusColor = (status) => {
    switch (status) {
      case "NEEDS REPLACEMENT":
        return "red";
      case "REPLACE SOON":
        return "orange";
      case "CONSIDER REPLACEMENT":
        return "gold";
      default:
        return "green";
    }
  };

  const getReplacementStatusIcon = (status) => {
    switch (status) {
      case "NEEDS REPLACEMENT":
        return <CloseCircleOutlined />;
      case "REPLACE SOON":
        return <WarningOutlined />;
      case "CONSIDER REPLACEMENT":
        return <InfoCircleOutlined />;
      default:
        return <CheckCircleOutlined />;
    }
  };

  return (
    <>
      <Card
        title="Egg Collections Dashboard"
        extra={
          <>
            <Space>
              <Select
                placeholder="Filter by url"
                allowClear
                style={{ width: 200 }}
                value={filters.urlToUse}
                onChange={(val) => handleFilterChange("urlToUse", val)}
              >
                {urlOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
              <Select
                placeholder="Filter by Batch"
                allowClear
                style={{ width: 200 }}
                value={filters.batch}
                onChange={(val) => handleFilterChange("batch", val)}
              >
                {batchOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
              <RangePicker
                value={filters.dateRange}
                onChange={(dates) => handleFilterChange("dateRange", dates)}
                presets={[
                  {
                    label: "Today",
                    value: [dayjs().startOf("day"), dayjs().endOf("day")],
                  },
                  {
                    label: "This Week",
                    value: [dayjs().startOf("week"), dayjs().endOf("week")],
                  },
                  {
                    label: "Last 14 Days",
                    value: [dayjs().subtract(14, "day"), dayjs()],
                  },
                  {
                    label: "This Month",
                    value: [dayjs().startOf("month"), dayjs().endOf("month")],
                  },
                  {
                    label: "This Year",
                    value: [dayjs().startOf("year"), dayjs().endOf("year")],
                  },
                ]}
              />
              <Tooltip title="Refresh">
                <Button icon={<ReloadOutlined />} onClick={fetchCollections} />
              </Tooltip>
              <Button onClick={resetFilters}>Reset</Button>
              <Button onClick={exportToCSVs}>
                <span className="feather icon-file-text"></span> Export CSV
              </Button>
              <a ref={csvLinkRef} style={{ display: "none" }} />

              <Link to="/eggs/collection">
                <Button>
                  <PlusOutlined /> Collect Eggs
                </Button>
              </Link>
            </Space>
          </>
        }
      >
        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <EggStatCard title="Normal" data={categoryData.normal} />
          </Col>

          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <EggStatCard title="Broken" data={categoryData.broken} />
          </Col>

          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <EggStatCard title="Double Yolk" data={categoryData.doubleYolk} />
          </Col>

          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <EggStatCard title="Medium" data={categoryData.medium} />
          </Col>

          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <EggStatCard title="Spoilt" data={categoryData.spoilt} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <EggStatCard title="Total Collected" data={totalCollected} />
          </Col>
        </Row>

        <Divider />

        <Table
          width="100%"
          columns={columns}
          dataSource={data}
          size="small"
          rowKey="id"
          loading={loading}
          scroll={{ x: "max-content" }}
          pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        />
      </Card>
    </>
  );
};

export default EggCollectionsDashboard;
